import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import {
  MeasurementSessionStatus,
  TERMINAL_SESSION_STATUSES,
  type MeasurementQrCode,
  type MeasurementSessionStatus as SessionStatus,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useLocale } from '@/hooks/useLocale';
import { apiErrorMessage } from '@/services/apiClient';
import { formatDateTime } from '@/utils/format';
import {
  useCancelMeasurementSessionMutation,
  useIssueQrCodeMutation,
  useMeasurementSessionQuery,
  useRetakeMeasurementSessionMutation,
} from '@/modules/measurements/api/measurements.api';
import { useSessionStream } from '@/modules/measurements/api/useSessionStream';
import { useCreateFaceSessionMutation } from '../api/face.api';
import { FaceReviewPanel } from './FaceReviewPanel';

/** How often the desk asks where the phone has got to, if the status stream cannot be kept open. */
const POLL_MS = 3_000;

/**
 * Handing a face analysis to a phone through the same one-time code the PD measurement uses.
 *
 * There is one handover mechanism in this product and this is it: a session, a code that redeems
 * once, a capability scoped to that session, and a desk that watches. What differs is only the
 * session's purpose, which the phone reads from its capability — never from the link it followed.
 *
 * The phone suggests. This dialog is where a person decides, and the deciding is the same act
 * whichever device held the camera.
 */
export function FacePhoneHandoverDialog({
  customerId,
  isOpen,
  onClose,
}: {
  customerId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const create = useCreateFaceSessionMutation();
  const issue = useIssueQrCodeMutation();
  const cancel = useCancelMeasurementSessionMutation();
  const retake = useRetakeMeasurementSessionMutation();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [code, setCode] = useState<MeasurementQrCode | null>(null);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  // Which opening of the dialog a request belongs to. An answer for an opening that has since closed is not this one's.
  const opening = useRef(0);
  // The latest code request. An older answer arriving after a newer request must not bring its code back.
  const codeRequest = useRef(0);
  // Sessions this dialog has already asked to cancel; a second request for the same one is refused by the server.
  const ended = useRef(new Set<string>());

  const end = (id: string) => {
    if (ended.current.has(id)) return;
    ended.current.add(id);
    cancel.mutate(id);
  };

  const lastStatus = useRef<SessionStatus | null>(null);
  const live = isOpen && !isEnded(lastStatus.current);
  // The stream prompts a refetch on every change; polling steps in only if the stream cannot stay open.
  const transport = useSessionStream(sessionId, live);
  const session = useMeasurementSessionQuery(
    sessionId,
    live && transport === 'polling' ? POLL_MS : false,
  );
  const current = session.data?.status ?? code?.session.status ?? null;
  lastStatus.current = current;

  /** Asks for a code for this opening's session. An answer for a closed opening, or for a superseded request, changes nothing. */
  const requestCode = (id: string) => {
    const forOpening = opening.current;
    const forRequest = ++codeRequest.current;
    const stillWanted = () => opening.current === forOpening && codeRequest.current === forRequest;

    setError(null);
    void issue
      .mutateAsync(id)
      .then(async (issued) => {
        if (!stillWanted()) return;
        setCode(issued);
        const image = await QRCode.toDataURL(issued.qrUrl, { margin: 1, width: 240 });
        if (stillWanted()) setQrImage(image);
      })
      .catch((caught: unknown) => {
        if (stillWanted()) setError(apiErrorMessage(caught) ?? t('face.errors.code'));
      });
  };

  useEffect(() => {
    // Effects run twice in development; one dialog opening is one session.
    if (!isOpen || started.current) return;
    started.current = true;
    const thisOpening = ++opening.current;

    // mutateAsync, not mutate: a call's own callbacks are dropped once a later opening calls again, and a late answer must still be seen so its session is ended.
    void create
      .mutateAsync({ customerId, requestedSource: 'PHONE_QR', opening: thisOpening })
      .then((created) => {
        // Closed while the session was being made: end it rather than issue a code nobody will scan.
        if (opening.current !== thisOpening) {
          end(created.id);
          return;
        }
        setSessionId(created.id);
        requestCode(created.id);
      })
      .catch((caught: unknown) => {
        if (opening.current === thisOpening) {
          setError(apiErrorMessage(caught) ?? t('face.errors.record'));
        }
      });
    // The mutations are stable for the dialog's life; only opening it should start a session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, customerId]);

  /**
   * Closing, and what it means for the session.
   *
   * An open session is ended, including one whose status has not been read yet — it was only just made. A finished one is
   * left alone. And the opening is over: whatever its requests answer from now on belongs to no dialog.
   */
  const close = (known?: SessionStatus) => {
    const effective = known ?? current;
    if (sessionId && !isEnded(effective)) end(sessionId);
    opening.current += 1;
    started.current = false;
    setSessionId(null);
    setCode(null);
    setQrImage(null);
    setError(null);
    onClose();
  };

  const waiting = current === MeasurementSessionStatus.WAITING_FOR_DEVICE;
  const underReview =
    current === MeasurementSessionStatus.AWAITING_REVIEW && session.data?.faceCandidate
      ? session.data.faceCandidate
      : null;
  const codeExpired = Boolean(code && waiting && Date.now() > Date.parse(code.tokenExpiresAt));
  const isValidation = session.data?.isValidation ?? code?.session.isValidation ?? false;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => close()}
      title={t('face.phone.title')}
      description={t('face.phone.description')}
      footer={
        <>
          {waiting ? (
            <Button
              variant="secondary"
              onClick={() => sessionId && requestCode(sessionId)}
              isLoading={issue.isPending}
            >
              {t('measurements.phone.newCode')}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => close()}>
            {current && !isEnded(current) ? t('measurements.phone.cancel') : t('common.close')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="error">{error}</Alert> : null}
        {isValidation ? <Alert tone="warning">{t('face.validationMode')}</Alert> : null}

        {waiting && qrImage && !codeExpired ? (
          <figure className="flex flex-col items-center gap-3">
            <img
              src={qrImage}
              alt={t('measurements.phone.qrAlt')}
              width={240}
              height={240}
              className="border-line rounded-md border bg-white p-2"
            />
            {code ? (
              <figcaption className="text-muted text-xs">
                {t('measurements.phone.validUntil', {
                  time: formatDateTime(code.tokenExpiresAt, locale),
                })}
              </figcaption>
            ) : null}
          </figure>
        ) : null}

        <p className="text-ink text-sm font-medium" role="status" aria-live="polite">
          {codeExpired ? t('measurements.phone.codeExpired') : t(statusMessage(current))}
        </p>

        {underReview && sessionId ? (
          <FaceReviewPanel
            sessionId={sessionId}
            candidate={underReview}
            // The confirmation's own answer says the session is finished, so closing does not cancel it.
            onDone={(completed) => close(completed.status)}
            onRetake={() =>
              retake.mutate(sessionId, {
                onSuccess: () => void session.refetch(),
                onError: (caught) => setError(apiErrorMessage(caught) ?? t('face.errors.record')),
              })
            }
          />
        ) : null}

        {current && !isEnded(current) ? (
          <p className="text-muted text-xs">{t('measurements.phone.closeEnds')}</p>
        ) : null}
      </div>
    </Modal>
  );
}

function isEnded(status: SessionStatus | null): boolean {
  return status !== null && TERMINAL_SESSION_STATUSES.includes(status);
}

function statusMessage(status: SessionStatus | null): string {
  switch (status) {
    case null:
    case MeasurementSessionStatus.CREATED:
      return 'measurements.phone.status.preparing';
    case MeasurementSessionStatus.WAITING_FOR_DEVICE:
      return 'measurements.phone.status.waiting';
    case MeasurementSessionStatus.CAPTURING:
      return 'face.phone.status.capturing';
    case MeasurementSessionStatus.AWAITING_REVIEW:
      return 'face.phone.status.review';
    case MeasurementSessionStatus.COMPLETED:
      return 'face.phone.status.completed';
    case MeasurementSessionStatus.CANCELLED:
      return 'measurements.phone.status.cancelled';
    case MeasurementSessionStatus.EXPIRED:
      return 'measurements.phone.status.expired';
    case MeasurementSessionStatus.FAILED:
      return 'measurements.phone.status.failed';
  }
}
